// Draws the power-up plants the simulation owns. Reads sim state, never
// mutates it (00-architecture.md §1); placement is the shared `placeOnSurface`,
// fed `powerUpFrame` (which handles a plant recessed into a crater), and the
// model comes from the slot's `POWERUP_DEFS` row.

import { type CookedModel, ModelRenderer, poseWorlds } from "../model";
import { multiply, type Mat4 } from "../math";
import { powerUpFrame, type PowerUp, type PowerUpId } from "../game/powerups";
import { POWERUP_DEFS, type PowerUpName } from "../game/defs";
import { placeOnSurface } from "./bug-renderer";

/** Clip name and time for a power-up's current state. */
interface Appearance {
  readonly clip: string;
  readonly time: number;
  readonly loop: boolean;
}

/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */
function duration(model: CookedModel, clip: string): number {
  return model.anims.get(clip)?.duration ?? 0;
}

/**
 * `time` is the wall clock, which only the looping idle needs — the one-shot
 * clips are stretched over the sim state's own progress, so the sim keeps
 * owning the timing (`grow` clamped while growing, `pick`/`die` clamped on the
 * way out, exactly the spawner renderer's shape).
 */
function appearance(p: PowerUp, model: CookedModel, time: number): Appearance {
  const state = p.state;
  switch (state.kind) {
    case "growing":
      return { clip: "grow", time: state.progress * duration(model, "grow"), loop: false };
    case "idle":
      return { clip: "idle", time, loop: true };
    case "taken":
      return { clip: "pick", time: state.progress * duration(model, "pick"), loop: false };
    case "killed":
      return { clip: "die", time: state.progress * duration(model, "die"), loop: false };
    default: {
      const unreachable: never = state;
      throw new Error(`unhandled power-up state ${JSON.stringify(unreachable)}`);
    }
  }
}

export class PowerUpRenderer {
  constructor(private readonly models: Record<PowerUpName, CookedModel>) {}

  draw(
    gl: WebGL2RenderingContext,
    renderer: ModelRenderer,
    powerups: ReadonlyMap<PowerUpId, PowerUp>,
    cubeModel: Mat4,
    viewProj: Mat4,
    time: number,
  ): void {
    for (const p of powerups.values()) {
      const name = POWERUP_DEFS[p.slot].model;
      const model = this.models[name];
      const look = appearance(p, model, time);
      renderer.draw(
        gl,
        model,
        poseWorlds(model, look.clip, look.time, look.loop),
        multiply(cubeModel, placeOnSurface(powerUpFrame(p), name, 1)),
        viewProj,
      );
    }
  }
}
